import { formatDateTime } from "@/utils/dateTime";
import { Card, Image, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { RecordDetails, type DetailField } from "./RecordDetails";
import type { GoodsDetail, GoodsSku } from "@/types/shopGoods";
import { goodsAuditStatuses, goodsPriceLabel, money } from "./goodsLabels";
import { getGoodsPricing } from "@/utils/goodsPricing";
import styles from "./ProductDialog.module.css";

const detailTagColors = ["geekblue", "cyan", "purple", "green", "orange", "magenta", "blue", "volcano"];

const fields: DetailField<GoodsDetail>[] = [
  { key: "id", label: "商品ID" },
  { key: "goodsName", label: "商品名称" },
  { key: "goodsSubtitle", label: "副标题" },
  {
    key: "mainImage",
    label: "主图",
    render: (data) =>
      data.mainImage ? (
        <Image
          width={96}
          height={96}
          style={{ objectFit: "cover", borderRadius: 8 }}
          src={data.mainImage}
        />
      ) : (
        "-"
      ),
  },
  {
    key: "goodsType",
    label: "商品类型",
    render: (data): React.ReactElement => (
      <Tag color={data.goodsType === 1 ? "blue" : data.goodsType === 2 ? "purple" : "default"}>
        {data.goodsType === 1 ? "自营" : data.goodsType === 2 ? "入驻商家" : data.goodsTypeName || "未知"}
      </Tag>
    ),
  },
  {
    key: "stockType",
    label: "库存类型",
    render: (data): React.ReactElement => (
      <Tag color={["default", "green", "orange"][data.stockType] ?? "default"}>
        {({ 1: "现货", 2: "预售" })[data.stockType] ?? "未知"}
      </Tag>
    ),
  },
  { key: "merchantName", label: "商家名称" },
  { key: "selfSupplier", label: "供应商名称" },
  { key: "businessProductType", label: "经营产品类型", render: (data): string => data.businessProductTypeStr?.trim() || data.businessProductType?.trim() || "-" },
  {
    key: "logisticsType",
    label: "物流类型",
    render: (data): React.ReactElement => (
      <Tag color={["default", "green", "orange"][data.logisticsType] ?? "default"}>
        {["不包邮", "包邮", "偏远地区不包邮"][data.logisticsType] ?? "未知"}
      </Tag>
    ),
  },
  { key: "postage", label: "邮费", render: (data) => money(data.postage) },
  {
    key: "remoteAreaList",
    label: "偏远地区",
    render: (data): string => data.remoteAreaList?.map((area) => area.name).join("、") || "-",
  },
  {
    key: "isSevenReturn",
    label: "七天无理由",
    render: (data) => (data.isSevenReturn === 1 ? "支持" : "不支持"),
  },
  {
    key: "isTimingShelf",
    label: "定时上架",
    render: (data) => (data.isTimingShelf === 1 ? "开启" : "关闭"),
  },
  { key: "timingShelfTime", label: "定时上架时间", render: (data) => formatDateTime(data.timingShelfTime) },
  { key: "price", label: "销售价格", render: goodsPriceLabel },
  {
    key: "marketPrice",
    label: "市场原价",
    render: (data) => money(getGoodsPricing(data).marketPrice),
  },
  { key: "stock", label: "可用库存", render: (data) => getGoodsPricing(data).stock ?? "-" },
  {
    key: "isShelf",
    label: "上架状态",
    render: (data): React.ReactElement => (
      <Tag color={data.isShelf === 1 ? "green" : "default"}>
        {data.isShelf === 1 ? "上架" : "下架"}
      </Tag>
    ),
  },
  {
    key: "auditStatus",
    label: "审核状态",
    render: (data): React.ReactElement => (
      <Tag color={data.goodsType === 1 ? "default" : ["default", "orange", "green", "red"][data.auditStatus] ?? "default"}>
        {data.goodsType === 1 ? "无需审核" : goodsAuditStatuses[data.auditStatus] ?? "未知"}
      </Tag>
    ),
  },
  { key: "auditTime", label: "审核时间", render: (data) => formatDateTime(data.auditTime) },
  { key: "auditRemark", label: "审核备注" },
  {
    key: "isMultiSpecification",
    label: "规格模式",
    render: (data): React.ReactElement => (
      <Tag color={data.isMultiSpecification === 1 ? "purple" : "cyan"}>
        {data.isMultiSpecification === 1 ? "多规格" : "单规格"}
      </Tag>
    ),
  },
  {
    key: "aftersaleType",
    label: "售后保障",
    render: (data) =>
      data.aftersaleType === 1 ? "支持七天无理由" : "不支持七天无理由",
  },
  { key: "salesNum", label: "商品总销量" },
  { key: "virtualSales", label: "虚拟销量" },
  { key: "sort", label: "排序" },
  { key: "belongUserId", label: "归属用户ID" },
  { key: "userId", label: "商家用户ID" },
  { key: "createBy", label: "创建人" },
  { key: "createTime", label: "创建时间", render: (data) => formatDateTime(data.createTime) },
  { key: "updateBy", label: "更新人" },
  { key: "updateTime", label: "更新时间", render: (data) => formatDateTime(data.updateTime) },
  { key: "remark", label: "备注" },
];
const skuColumns: ColumnsType<GoodsSku> = [
  { title: "SKU ID", dataIndex: "id" },
  { title: "商品ID", dataIndex: "goodsId" },
  {
    title: "SKU 名称",
    dataIndex: "skuName",
    render: (_, row, index): React.ReactElement | string => row.skuName ? (
      <Tag color={detailTagColors[index % detailTagColors.length]}>{row.skuName}</Tag>
    ) : "-",
  },
  {
    title: "规格值名称",
    dataIndex: "skuSpecValueNames",
    render: (_, row, index): React.ReactElement | string => row.skuSpecValueNames ? (
      <Tag color={detailTagColors[index % detailTagColors.length]}>{row.skuSpecValueNames}</Tag>
    ) : "-",
  },
  { title: "规格值ID", dataIndex: "skuSpecValueIds" },
  { title: "SKU 编码", dataIndex: "skuCode" },
  { title: "条形码", dataIndex: "barCode" },
  { title: "销售价格", dataIndex: "price", render: money },
  { title: "市场原价", dataIndex: "marketPrice", render: money },
  { title: "结算价", dataIndex: "costPrice", render: money },
  { title: "库存", dataIndex: "stock" },
  { title: "库存预警", dataIndex: "stockWarn" },
  { title: "实际销量", dataIndex: "sales" },
  { title: "虚拟销量", dataIndex: "virtualSales" },
  { title: "重量（kg）", dataIndex: "weight" },
  { title: "体积（m³）", dataIndex: "volume" },
  {
    title: "SKU 图片",
    render: (_, row) =>
      row.skuImage ? (
        <Image
          width={64}
          height={64}
          style={{ objectFit: "cover" }}
          src={row.skuImage}
        />
      ) : (
        "-"
      ),
  },
  {
    title: "状态",
    render: (_, row): React.ReactElement => (
      <Tag color={row.status === 1 ? "green" : "default"}>
        {row.status === 1 ? "启用" : "禁用"}
      </Tag>
    ),
  },
  { title: "排序", dataIndex: "sort" },
  { title: "创建人", dataIndex: "createBy" },
  { title: "创建时间", dataIndex: "createTime", render: formatDateTime },
  { title: "更新人", dataIndex: "updateBy" },
  { title: "更新时间", dataIndex: "updateTime", render: formatDateTime },
  { title: "备注", dataIndex: "remark" },
];
const detailGroups: { title: string; keys: (keyof GoodsDetail)[] }[] = [
  {
    title: "基本信息",
    keys: [
      "id",
      "goodsName",
      "goodsSubtitle",
      "mainImage",
      "goodsType",
      "merchantName",
      "selfSupplier",
      "businessProductType",
      "sort",
    ],
  },
  {
    title: "价格与库存",
    keys: [
      "stockType",
      "isMultiSpecification",
      "price",
      "marketPrice",
      "stock",
      "salesNum",
      "virtualSales",
    ],
  },
  {
    title: "配送与售后",
    keys: ["logisticsType", "postage", "remoteAreaList", "isSevenReturn", "aftersaleType"],
  },
  {
    title: "上架与审核",
    keys: [
      "isShelf",
      "isTimingShelf",
      "timingShelfTime",
      "auditStatus",
      "auditTime",
      "auditRemark",
    ],
  },
];
const groupedKeys = new Set(detailGroups.flatMap((group) => group.keys));
const recordFields = fields.filter((field) => !groupedKeys.has(field.key));

export function GoodsDetailPanel({
  detail,
}: {
  detail: GoodsDetail;
}): React.ReactElement {
  return (
    <div className={styles.detailContent}>
      {detailGroups.map((group) => (
        <Card key={group.title} title={group.title}>
          <RecordDetails
            data={detail}
            fields={fields.filter((field) =>
              group.keys.includes(field.key) &&
              (field.key !== "remoteAreaList" || detail.logisticsType === 2) &&
              (field.key !== "businessProductType" || detail.goodsType === 2) &&
              (field.key !== "merchantName" || detail.goodsType !== 1) &&
              (field.key !== "selfSupplier" || detail.goodsType === 1) &&
              !["isSevenReturn", "aftersaleType", "isTimingShelf", "timingShelfTime"].includes(field.key)
            ).map((field) => field.key === "goodsType" && detail.goodsType === 2 ? { ...field, label: "商家类型" } : field)}
          />
        </Card>
      ))}
      <Card title="轮播图">
        <Space wrap>
          {detail.slideList?.length
            ? detail.slideList.map((slide) => (
                <div key={slide.id ?? slide.slideImage}>
                  <Image
                    width={120}
                    height={96}
                    style={{ objectFit: "cover", borderRadius: 6 }}
                    src={slide.slideImage}
                  />
                  <p>排序：{slide.sort ?? 0}</p>
                </div>
              ))
            : "暂无轮播图"}
        </Space>
      </Card>
      <Card title="规格与分类">
        <p>
          规格：
          {detail.specRelList?.length
            ? detail.specRelList.map((spec, index) => (
                <Tag key={spec.specId} color={detailTagColors[index % detailTagColors.length]}>{spec.specName || spec.specId}</Tag>
              ))
            : "未配置规格"}
        </p>
        <p>
          分类：
          {detail.categoryRelList?.length
            ? detail.categoryRelList.map((category, index) => (
                <Tag key={category.categoryId} color={detailTagColors[(index + (detail.specRelList?.length ?? 0)) % detailTagColors.length]}>
                  {category.categoryName || category.categoryId}
                </Tag>
              ))
            : "未配置分类"}
        </p>
      </Card>
      <Card title="SKU 列表">
        <Table<GoodsSku>
          rowKey={(row) => String(row.id ?? row.skuSpecValueIds ?? row.skuName)}
          dataSource={detail.skuList ?? []}
          columns={skuColumns}
          pagination={false}
          scroll={{ x: "max-content" }}
        />
      </Card>
      <Card title="商品详情">
        {detail.goodsDesc ? (
          <iframe
            title="商品详情内容"
            sandbox=""
            referrerPolicy="no-referrer"
            srcDoc={detail.goodsDesc}
            style={{ width: "100%", height: 320, border: 0 }}
          />
        ) : (
          "暂无详情"
        )}
      </Card>
      <Card title="记录信息">
        <RecordDetails data={detail} fields={recordFields} />
      </Card>
    </div>
  );
}
